import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
    fetchNotifications,
    updateNotifications,
} from "./notification.slice";
import type { NotificationSettings } from "@shyne/shared/api";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export function useNotificationSettings() {
    const dispatch = useAppDispatch();
    const { data, loading, saving, error } = useAppSelector((s) => s.notification);
    const [hasRequested, setHasRequested] = useState(false);

    const fetchSettings = useCallback(() => {
        setHasRequested(true);
        void dispatch(fetchNotifications());
    }, [dispatch]);

    useEffect(() => {
        if (!data && !loading && !hasRequested) {
            fetchSettings();
        }
    }, [data, fetchSettings, hasRequested, loading]);

    const [email, setEmail] = useState({
        transaction: true,
        product: true,
        marketing: false,
    });

    const [sms, setSms] = useState({
        transaction: false,
        product: false,
    });
    const [groupChat, setGroupChat] = useState(true);

    useEffect(() => {
        if (!data) return;

        setEmail({
            transaction: data.transactionEmail,
            product: data.productEmail,
            marketing: data.marketingEmail,
        });
        setSms({
            transaction: data.transactionSms,
            product: data.productSms,
        });
        setGroupChat(data.groupChat);
    }, [data]);

    const handleSave = (settings: NotificationSettings) => dispatch(updateNotifications(settings));

    const onFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!data) return;

        const payload: NotificationSettings = {
            transactionEmail: email.transaction,
            productEmail: email.product,
            marketingEmail: email.marketing,
            transactionSms: sms.transaction,
            productSms: sms.product,
            groupChat,
        };

        try {
            await handleSave(payload).unwrap();
            toast.success("Notification settings saved.");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save settings.");
        }
    };

    return {
        settings: data,
        loading,
        saving,
        error,
        retryFetch: fetchSettings,
        onFormSubmit,
        handleSave,
        email,
        setEmail,
        sms,
        setSms,
        groupChat,
        setGroupChat,
    };
}
